import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useRole, usePermissions, useRoles } from '@/lib/api/hooks';
import { PageHeader } from '@/components/layout/page-header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { ArrowLeft, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';

export default function RoleEditorPage() {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { data: role, isLoading: roleLoading } = useRole(id!, { enabled: !!id });
    const { data: permissionsData, isLoading: permissionsLoading } = usePermissions();
    const { updatePermissions } = useRoles();

    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (role?.permissions) {
            setSelectedPermissions(role.permissions.map(p => p.id));
        }
    }, [role]);

    const handleTogglePermission = (permissionId: string) => {
        setSelectedPermissions(prev =>
            prev.includes(permissionId)
                ? prev.filter(id => id !== permissionId)
                : [...prev, permissionId]
        );
    };

    const handleToggleResource = (resource: string) => {
        if (!permissionsData?.grouped) return;

        const resourcePermissions = permissionsData.grouped[resource];
        const resourcePermissionIds = resourcePermissions.map(p => p.id);
        const allSelected = resourcePermissionIds.every(id => selectedPermissions.includes(id));

        if (allSelected) {
            setSelectedPermissions(prev => prev.filter(id => !resourcePermissionIds.includes(id)));
        } else {
            setSelectedPermissions(prev => [...new Set([...prev, ...resourcePermissionIds])]);
        }
    };

    const handleSave = async () => {
        if (!id) return;

        setIsSaving(true);
        try {
            await updatePermissions({ id, permissionIds: selectedPermissions });
            toast.success('Permissions updated successfully');
            navigate('/roles');
        } catch (error) {
            toast.error('Failed to update permissions');
        } finally {
            setIsSaving(false);
        }
    };

    if (roleLoading || permissionsLoading) {
        return <div className="text-center py-12">Loading...</div>;
    }

    if (!role) {
        return <div className="text-center py-12">Role not found</div>;
    }

    return (
        <div className="space-y-6">
            <PageHeader
                title={`Edit Role: ${role.name}`}
                description="Assign permissions to this role"
                action={
                    <div className="flex gap-2">
                        <Button variant="outline" onClick={() => navigate('/roles')}>
                            Back
                        </Button>
                        <Button onClick={handleSave} disabled={isSaving || role.isSystem}>
                            {isSaving ? 'Saving...' : 'Save Changes'}
                        </Button>
                    </div>
                }
            />

            {role.isSystem && (
                <Card className="border-yellow-200 bg-yellow-50">
                    <CardContent className="py-4">
                        <p className="text-sm text-yellow-800">
                            This is a system role. Permissions cannot be modified.
                        </p>
                    </CardContent>
                </Card>
            )}

            <div className="space-y-4">
                {permissionsData?.grouped && Object.entries(permissionsData.grouped)
                    .sort(([a], [b]) => a.localeCompare(b)) // Sort resources alphabetically
                    .map(([resource, permissions]) => {
                        const resourcePermissionIds = permissions.map(p => p.id);
                        const allSelected = resourcePermissionIds.every(id => selectedPermissions.includes(id));
                        const someSelected = resourcePermissionIds.some(id => selectedPermissions.includes(id));

                        // Sort permissions by action
                        const sortedPermissions = [...permissions].sort((a, b) => a.action.localeCompare(b.action));

                        return (
                            <Card key={resource}>
                                <CardHeader>
                                    <div className="flex items-center gap-3">
                                        <Checkbox
                                            checked={allSelected}
                                            onCheckedChange={() => handleToggleResource(resource)}
                                            disabled={role.isSystem}
                                            className={someSelected && !allSelected ? 'data-[state=checked]:bg-primary/50' : ''}
                                        />
                                        <div>
                                            <CardTitle className="text-lg capitalize">
                                                {resource.replace(/_/g, ' ')}
                                            </CardTitle>
                                            <CardDescription>
                                                {permissions.length} permission{permissions.length !== 1 ? 's' : ''}
                                            </CardDescription>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {sortedPermissions.map((permission) => (
                                            <div key={permission.id} className="flex items-center gap-2">
                                                <Checkbox
                                                    id={permission.id}
                                                    checked={selectedPermissions.includes(permission.id)}
                                                    onCheckedChange={() => handleTogglePermission(permission.id)}
                                                    disabled={role.isSystem}
                                                />
                                                <label
                                                    htmlFor={permission.id}
                                                    className="text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                                                >
                                                    {permission.description || permission.action}
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
            </div>
        </div>
    );
}
